// src/features/equipment/components/maintenance/MaintenanceCaseDrawer.jsx
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Stack,
  Portal,
  TextField,
} from '@mui/material';
import {
  Close as CloseIcon,
  Build as BuildIcon,
  CheckCircle as CheckCircleIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import MaintenanceStatusChip, { useFormatDateTime } from './MaintenanceStatusChip';
import MaintenanceTimeline from './MaintenanceTimeline';
import ServiceProviderContact from './ServiceProviderContact';
import { useEquipmentMaintenance } from '../../hooks/useEquipmentMaintenance';
import { downloadFileLink } from '../../../files/hooks/useFileUrl';
import { FileFullscreenViewer } from '../../../files/components/FileFullscreenViewer';
import { formatFileSize } from '../../../files/utils/formatFileSize';
import { FileDropZone } from '../../../files/upload';
import FileSourceButtons from '../../../files/components/FileSourceButtons';

function Detail({ label, children }) {
  if (!children) return null;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>{label}</Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{children}</Typography>
    </Box>
  );
}

/** Inline note box under the log, so a quick note doesn't need a dialog. */
function NoteComposer({ onSubmit }) {
  const { t } = useTranslation(['equipment']);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (!message.trim()) return;
    setSaving(true);
    const result = await onSubmit(message.trim());
    setSaving(false);
    if (result?.success) setMessage('');
  };

  return (
    <Box component="form" onSubmit={submit} display="flex" flexDirection="column" alignItems="flex-end" gap={2} mt={4}>
      <TextField
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={t('equipment:maintenance.fields.note_placeholder')}
        aria-label={t('equipment:maintenance.fields.note')}
        multiline
        minRows={2}
        size="small"
        fullWidth
        disabled={saving}
        slotProps={{ htmlInput: { maxLength: 5000 } }}
      />
      <Button type="submit" size="small" variant="outlined" disabled={saving || !message.trim()}>
        {t('equipment:maintenance.actions.add_note')}
      </Button>
    </Box>
  );
}

/**
 * Side drawer for one fault report or repair. `refreshKey` changes whenever
 * the case was modified elsewhere, so the drawer reloads it.
 */
export default function MaintenanceCaseDrawer({ maintenanceId, refreshKey, onClose, actions, openCases = [], onOpenCase }) {
  const { t } = useTranslation(['equipment', 'common']);
  const formatDateTime = useFormatDateTime();
  const { getCase } = useEquipmentMaintenance();
  const [maintenanceCase, setMaintenanceCase] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewingFile, setViewingFile] = useState(null);

  useEffect(() => {
    if (!maintenanceId) {
      setMaintenanceCase(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCase(maintenanceId).then((result) => {
      if (cancelled) return;
      if (result.success) setMaintenanceCase(result.data);
      else setError(result.error || t('equipment:maintenance.errors.load_failed'));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [maintenanceId, refreshKey, getCase, t]);

  const c = maintenanceCase;
  const isOpen = c && (c.status === 'reported' || c.status === 'in_progress');
  // A report on a device that's already pulled is settled by the pulled case, so it
  // can't be pulled or resolved on its own
  const blockedByPull = c?.status === 'reported' && c.equipment.status === 'in_maintenance';
  const coveringCase = blockedByPull ? openCases.find(o => o.status === 'in_progress') : null;

  return (
    <>
      <Drawer
        anchor="right"
        open={Boolean(maintenanceId)}
        // While a file is shown on top, Escape and focus belong to the viewer
        onClose={viewingFile ? undefined : onClose}
        disableEnforceFocus={Boolean(viewingFile)}
        slotProps={{ paper: { sx: { width: { xs: '100%', sm: 480 } } } }}
      >
        <Box display="flex" alignItems="flex-start" gap={2} px={6} pt={6} pb={4}>
          <Box flex={1} minWidth={0}>
            <Typography variant="body2" color="text.secondary">{c?.equipment.name}</Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ wordBreak: 'break-word', mt: 0.5 }}>{c?.title}</Typography>
            {c && (
              <Box display="flex" alignItems="center" gap={2} mt={2} flexWrap="wrap">
                <MaintenanceStatusChip status={c.status} />
                <Typography variant="caption" color="text.secondary">
                  {t('equipment:maintenance.reported_by_at', {
                    name: c.reported_by?.name ?? '–', date: formatDateTime(c.reported_at || c.created_at),
                  })}
                </Typography>
              </Box>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label={t('common:buttons.close')}><CloseIcon /></IconButton>
        </Box>

        {loading && !c && (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        )}
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {c && (
          // Files dropped anywhere on the case are uploaded and attached to it
          <FileDropZone
            onFiles={(files) => actions.uploadTo(c, files)}
            disabled={!actions.canMaintain}
            label={t('equipment:maintenance.drop_on_case')}
            sx={{ px: 6, pb: 8, overflowY: 'auto', flex: 1 }}
          >
            {blockedByPull && (
              <Alert
                severity="info"
                sx={{ mb: 4 }}
                action={coveringCase && (
                  <Button color="inherit" size="small" onClick={() => onOpenCase(coveringCase.maintenance_id)}>
                    {t('equipment:maintenance.actions.open_case')}
                  </Button>
                )}
              >
                {coveringCase
                  ? t('equipment:maintenance.covered_by_case', { title: coveringCase.title })
                  : t('equipment:maintenance.covered_by_pull')}
              </Alert>
            )}
            {actions.canMaintain && isOpen && (
              // One clear next step; closing without a repair is the quiet option at the end
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={6}>
                {c.status === 'reported' && !blockedByPull && (
                  <>
                    <Button size="small" variant="contained" startIcon={<BuildIcon />}
                            onClick={() => actions.openRepair(c)}>
                      {t('equipment:maintenance.actions.send_to_repair')}
                    </Button>
                    <Button size="small" variant="outlined" color="success" startIcon={<CheckCircleIcon />}
                            onClick={() => actions.openResolve(c)}>
                      {t('equipment:maintenance.actions.resolve')}
                    </Button>
                  </>
                )}
                {c.status === 'in_progress' && (
                  <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon />}
                          onClick={() => actions.openComplete(c)}>
                    {t('equipment:maintenance.actions.complete')}
                  </Button>
                )}
                <Box flex={1} />
                <Button size="small" color="error" onClick={() => actions.openCancel(c)}>
                  {c.status === 'in_progress'
                    ? t('equipment:maintenance.actions.cancel_repair')
                    : t('equipment:maintenance.actions.close_report')}
                </Button>
              </Box>
            )}

            <Stack spacing={4} mb={6}>
              <Detail label={t('equipment:maintenance.fields.fault_description')}>{c.description}</Detail>
              <ServiceProviderContact maintenanceCase={c} />
              <Detail label={t('equipment:maintenance.fields.work_done')}>{c.resolution}</Detail>
              <Detail label={t('equipment:maintenance.columns.outcome')}>
                {c.equipment_outcome && t(`equipment:maintenance.outcomes.${c.equipment_outcome}`)}
              </Detail>
            </Stack>

            <Divider />
            <Box display="flex" alignItems="center" mt={6} mb={2}>
              <Typography variant="subtitle2" flex={1}>
                {t('equipment:maintenance.attachments')} ({c.files.length})
              </Typography>
              {actions.canMaintain && (
                <FileSourceButtons onChoose={() => actions.openAttach(c)} onUpload={() => actions.pickFilesFor(c)} />
              )}
            </Box>
            {c.files.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('equipment:maintenance.no_attachments')}
              </Typography>
            ) : (
              <List dense disablePadding>
                {c.files.map((file) => (
                  <ListItem
                    key={file.maintenance_file_id}
                    disableGutters
                    disablePadding
                    secondaryAction={
                      <Box>
                        <Tooltip title={t('equipment:maintenance.actions.view')}>
                          <IconButton size="small" onClick={() => setViewingFile(file)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('equipment:maintenance.actions.download')}>
                          <IconButton size="small" onClick={() => downloadFileLink(file.file_id, { filename: file.filename })}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {actions.canMaintain && (
                          <Tooltip title={t('equipment:maintenance.actions.remove_file')}>
                            <IconButton size="small" onClick={() => actions.removeFile(c, file)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    }
                  >
                    <ListItemButton onClick={() => setViewingFile(file)} sx={{ px: 0.5, borderRadius: 1 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}><FileIcon fontSize="small" /></ListItemIcon>
                      <ListItemText
                        primary={file.filename}
                        secondary={`${formatFileSize(file.file_size)} · ${formatDateTime(file.added_at)}`}
                        slotProps={{ primary: { noWrap: true, sx: { pr: 13 } } }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}

            <Divider sx={{ mt: 6 }} />
            <Typography variant="subtitle2" mt={6} mb={2}>{t('equipment:maintenance.log')}</Typography>
            <MaintenanceTimeline entries={c.log_entries} onOpenCase={onOpenCase} />
            {actions.canMaintain && <NoteComposer onSubmit={(message) => actions.addNote(c, message)} />}
          </FileDropZone>
        )}
      </Drawer>
      {viewingFile && (
        // Portal puts it after the drawer in <body>, outside the part the drawer hides from screen readers
        <Portal>
          <FileFullscreenViewer
            fileId={viewingFile.file_id}
            filename={viewingFile.filename}
            filetype={viewingFile.filetype}
            onClose={() => setViewingFile(null)}
          />
        </Portal>
      )}
    </>
  );
}
